import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getToken, setToken } from "../api";
import FooterStudio from "./FooterStudio";

const emptyPackage = {
  title: "", slug: "", tagline: "", summary: "", days: 3, price: 600, currency: "USD",
  heroImage: "", region: "Sri Lanka", highlights: "", featured: true, published: true, seoTitle: "", seoDescription: "",
};
const emptyPost = {
  title: "", slug: "", excerpt: "", content: "", coverImage: "", author: "Vishmi Travels",
  category: "Journal", published: true, seoTitle: "", seoDescription: "",
};
const emptyPlace = {
  title: "", slug: "", summary: "", image: "", region: "", feeling: "", published: true, order: 0,
};
const emptyLogo = {
  kind: "logo", label: "", slug: "", title: "", subtitle: "", body: "", image: "", link: "/", order: 0, published: true,
};
const emptyContent = {
  kind: "page", label: "", slug: "", title: "", subtitle: "", body: "", image: "", video: "",
  link: "", linkLabel: "", link2: "", linkLabel2: "", order: 0, published: true,
};
const KINDS = ["logo", "page", "feature", "nav", "social", "footer", "clip", "loader", "chat"];
const emptyChat = {
  kind: "chat", label: "WhatsApp message", slug: "", title: "", order: 1, published: true,
};
const emptyLoader = {
  kind: "loader", label: "Page loader", slug: "page-loader", title: "Please wait..", subtitle: "", body: "",
  image: "/images/loader-pin.png", video: "/videos/beach.mp4", link: "", linkLabel: "", link2: "", linkLabel2: "", order: 1, published: true,
};

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(Boolean(getToken()));
  const [tab, setTab] = useState("inquiries");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [phone, setPhone] = useState(null);
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [draft, setDraft] = useState(null);

  async function load(next = tab) {
    if (next === "logos" || next === "content" || next === "loaders" || next === "whatsapp" || next === "footer") {
      const all = await api("/api/content/manage/all");
      if (next === "logos") setItems(all.filter((item) => item.kind === "logo" && item.slug !== "footer-logo"));
      else if (next === "loaders") setItems(all.filter((item) => item.kind === "loader"));
      else if (next === "footer") setItems(all);
      else if (next === "whatsapp") {
        setPhone(all.find((item) => item.slug === "whatsapp") || {
          kind: "social",
          slug: "whatsapp",
          label: "WhatsApp button",
          title: "",
          link: "",
          published: true,
          order: 4,
        });
        setItems(all.filter((item) => item.kind === "chat").sort((a, b) => (a.order || 0) - (b.order || 0)));
      } else setItems(all.filter((item) => !["logo", "loader", "chat", "footer", "social"].includes(item.kind)));
      return;
    }
    const path = {
      inquiries: "/api/inquiries",
      packages: "/api/packages/manage/all",
      posts: "/api/posts/manage/all",
      destinations: "/api/destinations/manage",
    }[next];
    setItems(await api(path));
  }

  useEffect(() => {
    if (!authed) return;
    load().catch((err) => {
      if (err.status === 401) {
        setToken("");
        setAuthed(false);
      } else setError(err.message);
    });
  }, [authed, tab]);

  async function onLogin(event) {
    event.preventDefault();
    setError("");
    try {
      const data = await api("/api/auth/login", { method: "POST", body: JSON.stringify(login) });
      setToken(data.token);
      setAuthed(true);
    } catch (err) {
      setError(err.message);
    }
  }

  function startCreate() {
    const blanks = {
      packages: emptyPackage,
      posts: emptyPost,
      destinations: emptyPlace,
      logos: emptyLogo,
      loaders: emptyLoader,
      content: emptyContent,
      whatsapp: { ...emptyChat, slug: `whatsapp-note-${Date.now()}`, order: items.length + 1 },
      inquiries: null,
    };
    setDraft({ ...blanks[tab] });
  }

  async function save(event) {
    event.preventDefault();
    const base = {
      packages: "/api/packages",
      posts: "/api/posts",
      destinations: "/api/destinations",
      inquiries: "/api/inquiries",
      logos: "/api/content",
      loaders: "/api/content",
      content: "/api/content",
      whatsapp: "/api/content",
    }[tab];
    const body = { ...draft };
    if (typeof body.highlights === "string") body.highlights = body.highlights.split(",").map((s) => s.trim()).filter(Boolean);
    if (draft._id) await api(`${base}/${draft._id}`, { method: "PUT", body: JSON.stringify(body) });
    else await api(base, { method: "POST", body: JSON.stringify(body) });
    setDraft(null);
    await load();
  }

  async function saveFooter(record) {
    const body = { ...record };
    delete body.createdAt;
    delete body.updatedAt;
    delete body.__v;
    if (body._id) await api(`/api/content/${body._id}`, { method: "PUT", body: JSON.stringify(body) });
    else await api("/api/content", { method: "POST", body: JSON.stringify(body) });
    await load();
  }

  async function removeFooter(id) {
    if (!id || !window.confirm("Delete this footer item?")) return false;
    await api(`/api/content/${id}`, { method: "DELETE" });
    await load();
    return true;
  }

  async function remove(id) {
    const base = {
      packages: "/api/packages",
      posts: "/api/posts",
      destinations: "/api/destinations",
      inquiries: "/api/inquiries",
      logos: "/api/content",
      loaders: "/api/content",
      content: "/api/content",
      whatsapp: "/api/content",
    }[tab];
    if (!window.confirm("Delete this record?")) return;
    await api(`${base}/${id}`, { method: "DELETE" });
    await load();
  }

  async function savePhone(event) {
    event.preventDefault();
    setError("");
    setPhoneSaved(false);
    const digits = String(phone?.title || "").replace(/\D/g, "");
    if (digits.length < 8) {
      setError("Enter the full WhatsApp number, including the country code.");
      return;
    }
    const body = {
      ...(phone || {}),
      kind: "social",
      slug: "whatsapp",
      label: phone?.label || "WhatsApp button",
      title: String(phone.title).trim(),
      link: `https://wa.me/${digits}`,
      order: phone?.order || 4,
      published: phone?.published !== false,
    };
    try {
      const saved = body._id
        ? await api(`/api/content/${body._id}`, { method: "PUT", body: JSON.stringify(body) })
        : await api("/api/content", { method: "POST", body: JSON.stringify(body) });
      setPhone(saved);
      setPhoneSaved(true);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  if (!authed) {
    return (
      <section className="section" style={{ maxWidth: 480, margin: "0 auto" }}>
        <p className="kicker">Studio</p>
        <h1 className="display" style={{ fontSize: 56 }}>Sign in</h1>
        <form className="form" onSubmit={onLogin} style={{ marginTop: 24 }}>
          <label>Email<input type="email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} required /></label>
          <label>Password<input type="password" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} required /></label>
          <button className="btn" type="submit">Enter</button>
          {error ? <p className="error">{error}</p> : null}
        </form>
        <p style={{ marginTop: 18 }}><Link to="/">Back to the site</Link></p>
      </section>
    );
  }

  const fields = draft ? Object.keys(draft).filter((key) => !["_id", "__v", "createdAt", "updatedAt", "itinerary"].includes(key)) : [];

  return (
    <div className="admin-shell">
      <aside className="admin-side">
        <Link to="/" className="brand"><span>Vishmi</span><span>Studio</span></Link>
        {["inquiries", "packages", "posts", "destinations", "logos", "loaders", "whatsapp", "footer", "content"].map((name) => (
          <button key={name} onClick={() => { setTab(name); setDraft(null); }}>{name}</button>
        ))}
        <button onClick={() => { setToken(""); setAuthed(false); navigate("/admin"); }}>Sign out</button>
      </aside>
      <section className="admin-main">
        <div className="admin-bar" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <h1 style={{ textTransform: "capitalize" }}>{tab}</h1>
          {tab !== "inquiries" && tab !== "footer" ? <button className="btn" onClick={startCreate}>Create</button> : null}
        </div>
        {tab === "loaders" ? (
          <p className="muted">Title is the typing line. Image is the pin. Video is the background. Turn published off to hide the loader, or delete the record.</p>
        ) : null}
        {tab === "content" ? (
          <p className="muted">Footer text, phones, and links are on the Footer tab. The WhatsApp number is on the WhatsApp tab.</p>
        ) : null}
        {tab === "whatsapp" ? (
          <p className="muted">The number here is the green button. Notes appear beside it: the first opens after 3 seconds, and the next one appears 10 seconds after a visitor closes a note. Turn published off to hide a note. Delete removes it.</p>
        ) : null}
        {error && tab !== "footer" ? <p className="error">{error}</p> : null}
        {tab === "footer" ? (
          <FooterStudio items={items} onSave={saveFooter} onDelete={removeFooter} />
        ) : tab === "whatsapp" ? (
          <>
            {phone ? (
              <form className="form" onSubmit={savePhone} style={{ margin: "20px 0", background: "white", padding: 20 }}>
                <h2 style={{ margin: "0 0 12px", fontSize: 22 }}>WhatsApp number</h2>
                <label>
                  Number on the button
                  <input
                    value={phone.title || ""}
                    onChange={(e) => { setPhoneSaved(false); setPhone({ ...phone, title: e.target.value }); }}
                    placeholder="+94 76784 5585"
                    inputMode="tel"
                  />
                </label>
                <p className="muted">Include the country code. The button opens {String(phone.title || "").replace(/\D/g, "").length >= 8 ? `https://wa.me/${String(phone.title).replace(/\D/g, "")}` : "wa.me once the number is complete"}.</p>
                <label>
                  Show button
                  <select value={String(phone.published !== false)} onChange={(e) => setPhone({ ...phone, published: e.target.value === "true" })}>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <button className="btn" type="submit">Save number</button>
                {phoneSaved ? <p className="muted">Number saved. The green button uses it on the next page.</p> : null}
              </form>
            ) : null}
            {draft ? (
              <form className="form" onSubmit={save} style={{ margin: "20px 0", background: "white", padding: 20 }}>
                <label>
                  Message
                  <textarea value={draft.title ?? ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} required />
                </label>
                <label>
                  Order
                  <input type="number" value={draft.order ?? 1} onChange={(e) => setDraft({ ...draft, order: Number(e.target.value) })} />
                </label>
                <label>
                  Published
                  <select value={String(Boolean(draft.published))} onChange={(e) => setDraft({ ...draft, published: e.target.value === "true" })}>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn" type="submit">Save</button>
                  <button className="btn ghost" type="button" onClick={() => setDraft(null)}>Cancel</button>
                </div>
              </form>
            ) : null}
            <table className="table">
              <thead>
                <tr>
                  <th>Message</th>
                  <th>Order</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item._id}>
                    <td>
                      <strong>{item.title}</strong>
                      {item.published ? null : <div className="muted">Hidden</div>}
                    </td>
                    <td>{item.order}</td>
                    <td className="row-actions">
                      <button onClick={() => setDraft({ ...item })}>Edit</button>
                      <button onClick={() => remove(item._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <>
        {draft ? (
          <form className="form" onSubmit={save} style={{ margin: "20px 0", background: "white", padding: 20 }}>
            {fields.map((key) => (
              <label key={key}>
                {key}
                {key === "content" || key === "summary" || key === "excerpt" || key === "message" || key === "body" ? (
                  <textarea value={draft[key] ?? ""} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
                ) : key === "kind" ? (
                  <select value={draft.kind || "page"} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                    {KINDS.map((kind) => <option key={kind} value={kind}>{kind}</option>)}
                  </select>
                ) : typeof draft[key] === "boolean" ? (
                  <select value={String(draft[key])} onChange={(e) => setDraft({ ...draft, [key]: e.target.value === "true" })}>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <input value={Array.isArray(draft[key]) ? draft[key].join(", ") : draft[key] ?? ""} onChange={(e) => setDraft({ ...draft, [key]: e.target.value })} />
                )}
              </label>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" type="submit">Save</button>
              <button className="btn ghost" type="button" onClick={() => setDraft(null)}>Cancel</button>
            </div>
          </form>
        ) : null}
        <table className="table">
          <thead>
            <tr>
              <th>Record</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item._id}>
                <td>
                  <strong>{item.label || item.title || item.name}</strong>
                  <div className="muted">{[item.kind, item.email, item.slug, item.status].filter(Boolean).join(" · ")}</div>
                  {item.message ? <div>{item.message}</div> : null}
                </td>
                <td className="row-actions">
                  <button onClick={() => setDraft({
                    ...item,
                    highlights: Array.isArray(item.highlights) ? item.highlights.join(", ") : item.highlights,
                  })}>Edit</button>
                  <button onClick={() => remove(item._id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          </>
        )}
      </section>
    </div>
  );
}
